/** @jsxImportSource @emotion/react */
import styled from "@emotion/styled";
import dayjs from "dayjs";
import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { useRecoilState } from "recoil";
import { protectedCategories } from "../../api/Categories";
import { TagApi } from "../../api/Tags";
import { TagsAtom } from "../../atoms/TagsAtom";
import { ICategoryType } from "../../generated/Api";
import { ITag } from "../../types/TagTypes";
import Tag from "../Tag";
import AutoCompleteCategory from "./AutoCompleteCategory";
import AutoCompleteTag, { ITagOption } from "./AutoCompleteTag";

const TagsContainer = styled.div`
  display: flex;
  width: 100%;
  margin-bottom: 10px;
  flex-wrap: wrap;
  flex-grow: 1;
  position: relative;
`;

const NewTagContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  padding: 16px 0px 0px 30px;
  background-color: #ffffff;
  color: #000;
`;

const Divider = styled.div`
  border-top: 1px solid #eaeaea;
  margin: 15px 0px 15px -30px;
`;

const NewTagTitle = styled.div`
  font-size: 12px;
  color: #979797;
  margin-bottom: 12px;
`;

const CreateTag = styled.div`
  width: 100%;
  height: fit-content;

  display: flex;
  flex-direction: row;
  align-items: center;

  padding: 10px 29px;
  cursor: pointer;

  border-radius: 0 0 4px 4px;

  background-color: #fff2e5;
  color: #fb972e;

  &:hover {
    background-color: #fb972e;
    color: #fff;
  }
`;

const CreateTagTitle = styled.span`
  width: 100%;

  font-size: 16px;
  line-height: 22px;
  font-weight: normal;

  .elipsis {
    max-width: 80px;
  }
`;

const NewTagContent = styled.div``;

interface Props {
  interestTagging?: boolean;
  tags: ITag[];
  placeholder?: string;
  disabled?: boolean;
  preserveSystemTags?: boolean;
  handleChoseTags: (tags: ITag[]) => void;
  cancelTagLoading?: boolean;
  contactTags?: ITag[];
}

export default function TagSelect(props: Props): JSX.Element {
  const {
    tags,
    preserveSystemTags,
    disabled,
    handleChoseTags,
    placeholder,
    interestTagging,
    contactTags,
  } = props;
  const [tagInputValue, setTagInputValue] = useState("");
  const [tagSuggestions, setTagSuggestions] = useState<ITagOption[]>();
  const [goToCategoryDropdown, setGoToCategoryDropdown] = useState(false);
  const [categoriesSuggestions, setCategoriesSuggestions] = useState<
    Array<{
      value: string;
      name: string;
      categoryType: ICategoryType;
    }>
  >();
  const [categoryInputValue, setCategoryInputValue] = useState("");
  const [tagsPool, setTagsPool] = useRecoilState(TagsAtom);
  const [loading, setLoading] = useState(false);
  const [noMoreTagsAvailable, setNoMoreTagsAvailable] = useState(false);

  async function getTags() {
    const tags = await TagApi.readAll({ getCampaigns: true });
    setTagsPool(tags);
  }

  useEffect(() => {
    if (props.cancelTagLoading) return;
    getTags();
  }, []);

  useEffect(() => {
    if (!tagsPool) return;

    let sortedTagNames = tagsPool.slice().sort((a, b) => {
      let isSameCategory = a.category.localeCompare(b.category, undefined, {
        sensitivity: "base",
      });

      if (isSameCategory === 0) {
        // Tags in the same category are compared here

        return a.name.localeCompare(b.name, undefined, {
          sensitivity: "base",
        });
      } else {
        return isSameCategory;
      }
    });

    // Tags could have a name that has a date generated by the system. If so,
    // the final array should be [...TAGS_ORDERED_BY_NAME, ...TAGS_ORDERED_BY_DATE]
    //
    let textNameTags: ITag[] = [];
    let dateNameTags: ITag[] = [];
    sortedTagNames.forEach((t) => {
      // Create an object with the tag name
      let tNameDate = dayjs(t.name, "MMM D, YYYY [@] h:mm:ss a");

      // Check if the date can be parsed from the name and put in the array accordingly
      if (!tNameDate.isValid()) textNameTags.push(t);
      else dateNameTags.push(t);
    });

    dateNameTags = dateNameTags.sort((a, b) => {
      let aNameDate = dayjs(a.name, "MMM D, YYYY [@] h:mm:ss a");
      let bNameDate = dayjs(b.name, "MMM D, YYYY [@] h:mm:ss a");

      return aNameDate.diff(bNameDate);
    });

    sortedTagNames = [...textNameTags, ...dateNameTags];

    if (preserveSystemTags) {
      sortedTagNames = sortedTagNames.filter(
        (tag) => tag.type !== ICategoryType.System
      );
    }

    if (!tags.length) {
      setTagSuggestions(sortedTagNames);
    } else {
      const tagSuggestionsWithoutSelectedOnes = sortedTagNames.filter(
        (tagSuggestion) =>
          tags.length > 0
            ? !tags.find((tag) => tag?._id === tagSuggestion?._id)
            : true
      );
      if (tagsPool.length && !tagSuggestionsWithoutSelectedOnes.length) {
        // Case where the account has tags in tagsPool, but, after filtering selected ones
        // didn't left tags available to select
        setNoMoreTagsAvailable(true);
      } else {
        setNoMoreTagsAvailable(false);
      }
      setTagSuggestions(tagSuggestionsWithoutSelectedOnes);
    }
  }, [tagsPool, preserveSystemTags, tags, handleChoseTags]);

  useEffect(() => {
    if (!tagsPool) return;

    const uniqueCategories = Array.from(
      new Set(tagsPool.map((t) => t.category))
    );

    const uniqueCategoriesObj = uniqueCategories.map((category) => ({
      value: tagsPool.find((tag) => tag.category === category)?._id!,
      name: category || "",
      categoryType: tagsPool.find((tag) => tag.category === category)?.type!,
    }));

    setCategoriesSuggestions(uniqueCategoriesObj);
  }, [tagsPool]);

  function handleTagValueSelect({ _id, name }: ITag) {
    if (name !== "" && tagsPool) {
      const alreadyIn = tags.find((tag) => tag._id === _id);
      const tagExists = tagsPool.find((tag) => tag._id === _id);

      if (!alreadyIn && tagExists) {
        handleChoseTags([...tags, tagExists]);
      }
      setTagInputValue("");
    }
  }

  function deleteTag(elem: ITag) {
    if (!(preserveSystemTags && elem.type === ICategoryType.System)) {
      handleChoseTags(tags.filter((tag) => tag._id !== elem._id));
    }
  }

  async function handleCategoryValueSelect(categoryName: string) {
    if (loading) return;

    setLoading(true);

    const isProtected = protectedCategories.find(
      (c) =>
        c.toLowerCase().replace(/\s\s+/g, " ").trim() ===
        categoryName.toLowerCase().replace(/\s\s+/g, " ").trim()
    );

    if (isProtected) {
      toast("Can't create category with system category name", {
        type: "info",
      });
    } else if (tagInputValue && categoryName) {
      const tagResponse = await TagApi.create({
        name: tagInputValue.replace(/\s\s+/g, " ").trim(),
        category: categoryName.replace(/\s\s+/g, " ").trim(),
      });
      if (tagResponse) {
        const newTag = {
          _id: tagResponse._id?.toString() || "",
          name: tagResponse.name,
          userUid: tagResponse.userUid,
          createdAt: tagResponse.createdAt,
          categoryId: tagResponse.categoryId.toString(),
          category: categoryName,
          type: ICategoryType.User,
        };
        setTagsPool((tags) => [...tags, newTag]);

        toast(`${categoryName} -> ${tagInputValue} tag succesfully created!`, {
          type: "success",
        });

        setGoToCategoryDropdown(false);
        setTagInputValue("");
        setCategoryInputValue("");

        // @ts-ignore
        handleChoseTags([...tags, newTag]);
      }
    } else {
      if (categoryName) {
        toast("Tag name can't be empty!", { type: "error" });
      } else {
        toast("Category name can't be empty!", { type: "error" });
      }
    }
    setLoading(false);
  }

  const NewTagHeader = () => {
    return (
      <NewTagContainer>
        <NewTagTitle>NEW TAG</NewTagTitle>
        <NewTagContent>{tagInputValue.trim()}</NewTagContent>
        <Divider />
        <NewTagTitle>SELECT CATEGORY</NewTagTitle>
      </NewTagContainer>
    );
  };

  const NoCategoriesFound = () => {
    return (
      <CreateTag
        onClick={() => handleCategoryValueSelect(categoryInputValue)}
        className={"link"}
      >
        <CreateTagTitle>
          <span>No categories found for </span>
          <span>"</span>
          <span className="elipsis">
            {categoryInputValue.trim().substring(0, 10) + "..."}
          </span>
          <span>"</span>
          <span>,</span> <b>click here to create.</b>
        </CreateTagTitle>
      </CreateTag>
    );
  };

  function viewCategoryDropdown() {
    setGoToCategoryDropdown(true);
  }

  const tagSuggestionsAfterCheckingContactTags = useMemo(() => {
    if (!tagSuggestions) return;
    if (!contactTags) return tagSuggestions;

    const filteringContactTagsFromSuggestions = tagSuggestions.filter(
      (ts) => !contactTags.find((ct) => ct._id === ts._id)
    );

    return filteringContactTagsFromSuggestions;
  }, [contactTags, tagSuggestions]);

  function TagOrCategoryDropdown() {
    if (!goToCategoryDropdown) {
      return (
        <AutoCompleteTag
          absolutePosition={interestTagging}
          selectedValue={tagInputValue}
          onValueSelect={handleTagValueSelect}
          onEnter={handleTagValueSelect}
          onValueChange={setTagInputValue}
          placeholder={placeholder || "Search for tag(s)"}
          suggestions={tagSuggestionsAfterCheckingContactTags}
          viewCategories={viewCategoryDropdown}
          noMoreTagsAvailable={noMoreTagsAvailable}
        />
      );
    } else {
      return (
        <AutoCompleteCategory
          absolutePosition={interestTagging}
          tagValue={tagInputValue}
          selectedValue={categoryInputValue}
          tagSuggestions={tagSuggestionsAfterCheckingContactTags}
          onValueSelect={(option) => handleCategoryValueSelect(option.name)}
          onEnter={(option) => handleCategoryValueSelect(option.name)}
          onValueChange={setCategoryInputValue}
          placeholder="Now enter or select a category"
          suggestions={categoriesSuggestions}
          dropdownHeader={NewTagHeader()}
          noResultsComponent={NoCategoriesFound()}
          handleCategoryValueSelect={handleCategoryValueSelect}
          onCancel={() => {
            setGoToCategoryDropdown(false);
            setTagInputValue("");
            setCategoryInputValue("");
          }}
          isCategoryComponent
        />
      );
    }
  }

  return (
    <div>
      <div style={{ pointerEvents: disabled ? "none" : "auto" }}>
        {TagOrCategoryDropdown()}
      </div>
      <TagsContainer>
        {tags
          .filter((t) => (interestTagging ? t.categoryId : t))
          .map((elm: ITag) => {
            return (
              <Tag
                key={`recipient_tag_tags_select${elm._id}`}
                tag={elm}
                preserveSystemTags={preserveSystemTags}
                deleteTag={deleteTag}
                disabled={disabled}
              />
            );
          })}
      </TagsContainer>
    </div>
  );
}
